import { env } from "@/lib/env";
import { ApiError } from "@altek/ui/lib/problem";
import { appErrorMessage, isAppErrorCode, keepsServerSentence } from "./error-messages";
import { requestSignal } from "./request-signal";
import { announceWrite } from "./write-signal";

/**
 * The ONLY HTTP layer in this application.
 *
 * Nothing else calls `fetch`. Every request in the app goes through here, which is what makes
 * the following true in one place instead of ninety: the CSRF header is attached to every
 * write, the response envelope is unwrapped, a 401 is turned into a session event exactly
 * once, and `x-request-id` reaches the error the user sees.
 *
 * AUTH MODEL. The session is an HttpOnly cookie. This code never reads it and never stores a
 * token — the browser attaches it. What it DOES do is ask for a new one when the 15-minute
 * access cookie runs out (`renewSession` below); the 30-day refresh cookie is still the
 * browser's, and JavaScript never sees either value. The only token JavaScript touches is the
 * CSRF value, which is deliberately readable because echoing it in a header is the whole point
 * of the double-submit pattern: an attacker's page can make the browser send the cookie, but
 * cannot read it to copy into a header.
 */

const CSRF_COOKIE = "csrf";
const CSRF_HEADER = "x-csrf-token";
const REQUEST_ID_HEADER = "x-request-id";
/** Sent by the API on every 429 and on an in-flight idempotency key (seconds). */
const RETRY_AFTER_HEADER = "retry-after";

/**
 * The header that makes cookie authentication happen at all.
 *
 * The API decides how to deliver a session from `x-app-platform`: a platform listed in
 * `AUTH_WEB_PLATFORMS` gets HttpOnly cookies, anything else gets the tokens in the response
 * body for a native client to store. Omit it and the browser is treated as a native client —
 * login returns 200 with an access token in JSON, no cookie is set, and every subsequent
 * request is a 401 `TOKEN_MISSING`. Nothing warns about it, and the only visible symptom is
 * that the application appears to sign you out immediately after signing you in.
 *
 * So it is attached to EVERY request rather than only to login: the platform is a property of
 * this client, not of one endpoint.
 */
const PLATFORM_HEADER = "x-app-platform";
const PLATFORM = "WEB";

/** Writes need the CSRF header; reads do not, and adding it would only widen what leaks. */
const WRITE_METHODS = new Set(["POST", "PUT", "PATCH", "DELETE"]);

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
  method?: HttpMethod;
  /** Serialised as JSON. Omit for a body-less request — see the content-type note below. */
  body?: unknown;
  /**
   * Appended as a query string; `undefined`, `null` and `""` entries are dropped and arrays
   * repeat the key.
   *
   * HARD RULE: query strings are never concatenated by the caller. `api.get("/x?page=" + page)`
   * skips the encoding this function does, and the first value containing a space, a `&` or a
   * Turkish character produces a request the server reads differently from the one intended.
   */
  params?: Record<string, string | number | boolean | null | undefined | Array<string | number>>;
  signal?: AbortSignal;
  /** Retry-unsafe writes carry one so a retried request is not applied twice. */
  idempotencyKey?: string;
  headers?: Record<string, string>;
  /**
   * A write that does NOT make the rest of the cache stale (1 Kasım 2026; `write-signal.ts`). Every other
   * successful write announces itself and the application refetches what is on screen. `true` for a POST that
   * is really a read (revealing a masked value, an import preview), for a high-frequency write that would
   * refetch the screen every few seconds (autosave, a chat message, a read mark), and nothing else.
   */
  quiet?: boolean;
}

/**
 * Writes that are quiet whoever calls them: they change WHO is asking, or reserve something nobody sees yet.
 *
 * A sign-in, a sign-out, a support session or a customer switch is followed by a clean cache, not a refetch:
 * refetching would ask with the NEXT identity's cookie (the AuthProvider removes the previous one's answers;
 * the support session reloads the page). An upload intent only reserves a file id; the write that shows up on a
 * screen is the one that links the file, and that one announces.
 */
const QUIET_WRITE_PATHS: readonly RegExp[] = [
  /^\/auth\/(?:login|logout|refresh|reauthenticate|accept-invite|session\/tenant)(?:\/|$)/,
  /^\/auth\/password-reset\//,
  /^\/support\/impersonation(?:\/|$)/,
  /^\/files\/upload-intent$/,
];

/** A successful write — not a quiet one — tells the cache (`write-signal.ts`) once its answer has been read. */
function announceSettledWrite(path: string, options: RequestOptions): void {
  const method = options.method ?? "GET";
  if (!WRITE_METHODS.has(method) || options.quiet) return;
  if (QUIET_WRITE_PATHS.some((pattern) => pattern.test(path))) return;
  announceWrite();
}

/** `{ data, meta? }` — the shape every successful response carries. */
export interface Envelope<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

export interface Page<T> {
  rows: T[];
  pagination: PaginationMeta;
  /**
   * The list's other `meta` keys, beside `pagination` — a count that belongs to the list but not
   * to the page (the converter's `activeCount`). Absent when the response carries none; each
   * reader narrows the key it knows, because the pagination fields never grow.
   */
  meta?: Readonly<Record<string, unknown>>;
}

export interface CursorPage<T> {
  rows: T[];
  next: string | null;
  hasMore: boolean;
}

/* ------------------------------------------------------------------ session signalling */

type SessionListener = (event: { reason: "expired" }) => void;
const sessionListeners = new Set<SessionListener>();

/**
 * A dropped session is an application-wide event, not a per-call error. The auth provider
 * subscribes once and moves the user to sign-in; without this, every query and mutation would
 * have to recognise a 401 and navigate, and whichever one forgot would leave the user staring
 * at an empty table.
 */
export function onSessionExpired(listener: SessionListener): () => void {
  sessionListeners.add(listener);
  return () => sessionListeners.delete(listener);
}

let expiryAnnounced = false;

function announceExpiry(): void {
  // Once per expiry, not once per in-flight request: a dashboard fires six queries and all six
  // fail together, and six redirects is a loop.
  if (expiryAnnounced) return;
  expiryAnnounced = true;
  for (const listener of sessionListeners) listener({ reason: "expired" });
}

/** Called by the auth provider after a successful sign-in so the guard above can fire again. */
export function resetSessionExpiry(): void {
  expiryAnnounced = false;
}

/* ------------------------------------------------------------------ silent renewal */

/**
 * ERİŞİM ÇEREZİ 15 DAKİKA, OTURUM 30 GÜN — 7 Ekim 2026, kullanılabilirlik turu.
 *
 * Sunucu erişim jetonunu 15 dakikalık, yenileme çerezini 30 günlük veriyor ve
 * `POST /auth/refresh` ikisini birlikte döndürüyor. Bu istemci yenilemeyi HİÇ çağırmıyordu: ilk
 * 401'i oturumun bittiği diye okuyup kişiyi giriş ekranına atıyordu. Sonuç, her 15 dakikada bir
 * açıklamasız çıkış — yarım kalan izin formu, bütçe satırı, sunum talebi kayboluyordu.
 *
 * Şimdi: süresi dolmuş ya da düşmüş erişim çerezi (`TOKEN_EXPIRED`, `TOKEN_MISSING`) bir kez
 * sessizce yenilenir ve istek BİR KEZ yeniden denenir. Reddedilen istek hiçbir şeyi
 * değiştirmeden döndüğü için (kimlik doğrulaması iş kuralından önce koşar) yazma isteğini tekrar
 * göndermek güvenli; aynı idempotency anahtarı da taşınır.
 *
 * TEK UÇUŞ. Panelim aynı anda altı sorgu atar ve altısı birlikte 401 alır; altı yenileme aynı
 * çerezi altı kez döndürmeye çalışırdı. Uçuştaki tek yenilemeyi hepsi bekler.
 *
 * İKİNCİ DÖNDÜRME YOK. Sunucu yenilemede eski oturum satırını iptal edip yenisini açar; yani bir
 * önceki erişim jetonu, süresi dolmamış olsa bile, o andan itibaren 401 `SESSION_REVOKED` alır —
 * ve o kod yenilenmez, kişiyi çıkışa atar. Tek uçuş bunu kapatmıyordu: yenileme BİTTİKTEN sonra
 * dönen bir 401 (eski çerezle gitmiş ama cevabı geç gelmiş bir sorgu; tarayıcının altı bağlantı
 * sınırında sırada bekleyen bir istek) ikinci bir yenileme başlatıyor, birincinin yeniden
 * denenen isteklerini öldürüyordu. O yüzden her istek kurulduğu andaki CSRF çerezini hatırlar
 * (`sessionMark`): sunucu her yenilemede yeni oturuma bağlı YENİ bir CSRF jetonu verdiği için
 * çerez değişmişse oturum bu istekten sonra zaten yenilenmiştir — bu sekmede ya da başka bir
 * sekmede. O zaman yenilenmez, istek yalnız yeniden gönderilir.
 *
 * İKİ SEKME AYNI ANDA YENİLERSE sunucu ikincisine `REFRESH_TOKEN_STALE` der (döndürmeden sonraki
 * iki dakikalık tolerans): öteki sekme çerezi zaten yeniledi. Ama bu cevap öteki sekmenin
 * `Set-Cookie`'sinden ÖNCE gelebilir — sunucu döndürmeyi yazıp yayınlarken ikinci istek çoktan
 * reddedilmiştir — ve hemen yeniden denemek ölü çerezle gitmek olurdu. Bu yüzden çerezin
 * değişmesi en çok `STALE_WAIT_MS` beklenir, sonra istek bir kez yeniden denenir; yeni çerez hiç
 * gelmediyse o denemenin 401'i oturumu bitirir.
 *
 * YAZMA İSTEĞİNİN TEKRARI CSRF başlığını çerezden YENİDEN okur (`send` başlıkları her seferinde
 * kurar): eski değer eski oturuma bağlıdır ve 403 alır. Gövde ve idempotency anahtarı AYNI kalır.
 *
 * DESTEK OTURUMUNDA KAPALI. Kimliğine bürünülen kişinin jetonunun yenilemesi yok; yenileme
 * çerezi yöneticinin kendisine ait. Burada yenilemek, isteği SESSİZCE yöneticinin kimliğiyle
 * tekrarlamak olurdu — kimin adına yazıldığını değiştiren bir yan etki. `AuthProvider` destek
 * oturumu süresince yenilemeyi kapatır; pencere kapanınca eskisi gibi giriş ekranına dönülür.
 */
const RENEWABLE_CODES = new Set(["TOKEN_EXPIRED", "TOKEN_MISSING"]);
/** Kendi 401'i yenileme denemesine yol açmaması gereken oturum uçları — aksi bir döngü. */
const SESSION_PATHS = new Set([
  "/auth/login",
  "/auth/refresh",
  "/auth/logout",
  // The sign-in's second step (17 Ekim 2026): no session to renew yet.
  "/auth/login/mfa",
  "/auth/login/mfa/enrol",
  "/auth/login/mfa/activate",
]);
const REFRESH_PATH = "/auth/refresh";
/** `REFRESH_TOKEN_STALE` sonrası öteki sekmenin çerezini en çok bu kadar bekler. */
const STALE_WAIT_MS = 5_000;
const STALE_POLL_MS = 50;

/**
 * OTURUMLA DEĞİL PAROLAYLA İLGİLİ 401'ler: adım adım doğrulamada yanlış yazılan parola
 * (`REAUTH_INVALID`), giriş, parola değiştirme ya da ikinci faktörde yanlış parola
 * (`INVALID_CREDENTIALS`). Kitin `isSessionExpired`'ı «`REAUTH_REQUIRED` dışındaki her 401»;
 * bu liste olmadan bir yazım hatası oturumu bitiriyor, doğrulama penceresi kendi hatasını
 * gösterirken uygulama giriş ekranına gidiyordu.
 */
const PASSWORD_VERDICTS = new Set([
  "INVALID_CREDENTIALS",
  "REAUTH_INVALID",
  "REAUTH_EXPIRED",
  /**
   * İKİ ADIMLI DOĞRULAMA (17 Ekim 2026, #31): yanlış yazılan kod ve süresi dolan giriş sorgusu da
   * yazılanla ilgili — oturumla değil. Bunlar «oturum bitti» sayılsaydı kurulumun ortasında yanlış
   * bir kod kişiyi çıkışa atardı (sunucu eskiden `TOKEN_INVALID` döndürüyordu, tam olarak bu oldu).
   */
  "MFA_CODE_INVALID",
  "MFA_CHALLENGE_INVALID",
]);

type Renewal = "renewed" | "rejected" | "unreachable";

let renewalEnabled = true;
let renewalInFlight: Promise<Renewal> | null = null;

/** `AuthProvider` destek oturumu boyunca kapatır, oturum normale dönünce açar. */
export function setSessionRenewal(enabled: boolean): void {
  renewalEnabled = enabled;
}

function mayRenew(path: string, code: string): boolean {
  if (!renewalEnabled || !RENEWABLE_CODES.has(code)) return false;
  const bare = (path.startsWith("/") ? path : `/${path}`).split("?")[0] ?? "";
  return !SESSION_PATHS.has(bare);
}

/**
 * Tarayıcının elindeki oturumun izi: CSRF çerezi — JavaScript'in okuyabildiği tek oturum çerezi.
 * Sunucu onu her girişte ve her yenilemede yeni oturuma bağlı olarak yeniden verir, yani değeri
 * değiştiyse kavanozdaki erişim çerezi de değişmiştir. Değerin kendisi hiçbir şeyi kanıtlamaz;
 * yalnız «aynı mı» diye sorulur.
 */
function sessionMark(): string | null {
  return readCookie(CSRF_COOKIE);
}

async function markMovesFrom(before: string | null): Promise<void> {
  for (let waited = 0; sessionMark() === before && waited < STALE_WAIT_MS; waited += STALE_POLL_MS) {
    await new Promise((resolve) => setTimeout(resolve, STALE_POLL_MS));
  }
}

async function attemptRenewal(): Promise<Renewal> {
  const headers: Record<string, string> = { accept: "application/json", [PLATFORM_HEADER]: PLATFORM };
  const mark = sessionMark();
  if (mark) headers[CSRF_HEADER] = mark;
  let response: Response;
  try {
    response = await fetch(buildUrl(REFRESH_PATH, undefined), {
      method: "POST",
      headers,
      credentials: "same-origin",
    });
  } catch {
    // Bağlantı yok: oturum belki hâlâ geçerli. Kişiyi çıkışa atmak yerine bağlantı hatası verilir.
    return "unreachable";
  }
  if (response.ok) return "renewed";
  if (response.status === 401) {
    const error = await parseError(response);
    if (error.code === "REFRESH_TOKEN_STALE") {
      await markMovesFrom(mark);
      return "renewed";
    }
  }
  return response.status >= 500 || response.status === 429 ? "unreachable" : "rejected";
}

/**
 * `sentWith`: isteğin kurulduğu andaki iz. Artık başkaysa oturum o istekten sonra yenilenmiştir
 * ve yeniden göndermek yeter — ikinci bir döndürme, birincinin isteklerini öldürürdü.
 */
function renewSession(sentWith: string | null): Promise<Renewal> {
  if (sessionMark() !== sentWith) return Promise.resolve("renewed");
  renewalInFlight ??= attemptRenewal().finally(() => {
    renewalInFlight = null;
  });
  return renewalInFlight;
}

/* ------------------------------------------------------------------ helpers */

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split("; ")) {
    if (part.startsWith(prefix)) return decodeURIComponent(part.slice(prefix.length));
  }
  return null;
}

function buildUrl(path: string, params: RequestOptions["params"]): string {
  const base = path.startsWith("/") ? `${env.apiBaseUrl}${path}` : `${env.apiBaseUrl}/${path}`;
  if (!params) return base;

  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === "") continue;
    if (Array.isArray(value)) {
      for (const item of value) search.append(key, String(item));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `${base}?${qs}` : base;
}

/**
 * `Retry-After` in seconds — either form HTTP allows (delta-seconds or an HTTP-date) — or `null`
 * for anything else. A garbage header must not invent a wait the server never asked for.
 */
function retryAfterSeconds(header: string | null): number | null {
  if (header === null) return null;
  const trimmed = header.trim();
  if (/^\d+$/.test(trimmed)) return Number(trimmed);
  const at = Date.parse(trimmed);
  if (Number.isNaN(at)) return null;
  return Math.max(0, Math.ceil((at - Date.now()) / 1000));
}

async function parseError(response: Response): Promise<ApiError> {
  const requestId = response.headers.get(REQUEST_ID_HEADER);
  let code = `HTTP_${response.status}`;
  let serverMessage: string | null = null;
  let details: unknown;

  try {
    const payload = (await response.json()) as { error?: { code?: string; message?: string; details?: unknown } };
    if (payload.error) {
      code = payload.error.code ?? code;
      serverMessage = payload.error.message ?? null;
      details = payload.error.details;
    }
  } catch {
    // A non-JSON error body (a proxy's HTML 502, an empty 504) is not a contract violation
    // worth surfacing as a parse failure — the status alone is enough to classify it.
  }

  /**
   * THE WAIT, even when the body lost it. The API puts it in both places; a proxy's own 429, or
   * a body without details, leaves only the header — and "bir süre bekleyin" without a number is
   * the sentence that sends somebody straight back to the button.
   */
  const retryAfter = retryAfterSeconds(response.headers.get(RETRY_AFTER_HEADER));
  const detailRecord =
    details !== null && typeof details === "object" && !Array.isArray(details)
      ? (details as Record<string, unknown>)
      : undefined;
  const canCarryWait = details === undefined || details === null || detailRecord !== undefined;
  if (retryAfter !== null && canCarryWait && detailRecord?.retryAfterSeconds === undefined) {
    details = { ...detailRecord, retryAfterSeconds: retryAfter };
  }

  /**
   * CODES THIS APPLICATION OWNS (`error-messages.ts`). The kit's `ApiError` phrases the codes it
   * knows and otherwise falls back to `serverMessage`; for a code only this application phrases,
   * that fallback becomes the application's sentence, so every screen already calling
   * `messageFor(error)` reads it without being edited. A code the kit knows keeps the server's
   * text untouched.
   */
  // onay-zinciri 20 Ekim 2026: `APPROVER_NOT_FOUND`'un sunucu cümlesi birim adlarını taşıyor — ezilmiyor.
  if (isAppErrorCode(code) && !keepsServerSentence(code)) {
    serverMessage =
      appErrorMessage(code, (details as Record<string, unknown> | undefined) ?? null) ?? serverMessage;
  }

  return new ApiError({ status: response.status, code, serverMessage, details, requestId });
}

/* ------------------------------------------------------------------ the request */

async function request<T>(path: string, options: RequestOptions = {}): Promise<Envelope<T>> {
  const response = await send(path, options, "application/json");
  const envelope = await readEnvelope<T>(response);
  /*
    Gövde OKUNDUKTAN sonra: duyurunun tazelemesi bir sonraki görevde başlıyor (`staleAfterWrite`), o sırada
    çağıranın mutasyonu kendi anahtarlarını çoktan geçersiz kılmış oluyor — aynı istek iki kez gitmiyor.
  */
  announceSettledWrite(path, options);
  return envelope;
}

async function readEnvelope<T>(response: Response): Promise<Envelope<T>> {
  if (response.status === 204) return { data: undefined as T };

  const text = await response.text();
  if (!text) return { data: undefined as T };
  return JSON.parse(text) as Envelope<T>;
}

/**
 * Bir isteği kurar, gönderir ve HATASIZ cevabı döndürür — gövdeyi okumadan.
 *
 * `request` ile `requestBlob`'un ortak yarısı. İkisinin de kurması gereken şey aynı: platform
 * başlığı, yazan yöntemlerde CSRF jetonu, `same-origin` çerez, iptal sinyali, ve düşen bir
 * cevabın `ApiError`'a çevrilmesi. Ayrıldıkları tek yer gövdeyi nasıl okudukları.
 */
async function send(
  path: string,
  options: RequestOptions,
  accept: string,
  renewed = false,
): Promise<Response> {
  // Oturum yenilenip istek yeniden gönderilince sinyal `options` ile taşınıyor (`withRequestSignal`).
  const ambientSignal = requestSignal();
  if (!options.signal && ambientSignal) options = { ...options, signal: ambientSignal };
  const method = options.method ?? "GET";
  const headers: Record<string, string> = {
    accept,
    [PLATFORM_HEADER]: PLATFORM,
    ...options.headers,
  };

  /**
   * A content-type header on a body-less request is a lie the server is entitled to reject —
   * and this one does, with a 400. So it is attached only when there is actually a body.
   */
  const hasBody = options.body !== undefined;
  if (hasBody) headers["content-type"] = "application/json";

  // Read on every attempt, the replay included: a renewal rotates the CSRF token with the session.
  const mark = sessionMark();
  if (WRITE_METHODS.has(method) && mark) headers[CSRF_HEADER] = mark;

  if (options.idempotencyKey) headers["idempotency-key"] = options.idempotencyKey;

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.params), {
      method,
      headers,
      // Same-origin in production and through the dev proxy, so the session cookie rides along
      // without `include` and without a CORS allowlist.
      credentials: "same-origin",
      ...(hasBody ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    // An aborted request is not a failure — it is the caller changing its mind, and turning it
    // into a visible error would flash a message every time someone types in a search box.
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    /**
     * `serverMessage` BOŞ — tarayıcının cümlesi bir SUNUCU cümlesi değil (8 Ekim 2026,
     * kullanılabilirlik turu). Burada `cause.message` yazıyordu; kitin `messageFor`'u sunucunun
     * okuyucuya yazdığı metni sözlükten ÖNCE gösteriyor, yani çıkışta bağlantı kopunca tost ham
     * «Failed to fetch» diyordu (Safari'de «Load failed»). Sunucu buraya hiç konuşmadı; söylenecek
     * olan kodun kendi cümlesi: «Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.»
     */
    throw new ApiError({ status: 0, code: "NETWORK", serverMessage: null });
  }

  if (!response.ok) {
    const error = await parseError(response);
    if (error.isSessionExpired && !PASSWORD_VERDICTS.has(error.code)) {
      if (!renewed && mayRenew(path, error.code)) {
        const outcome = await renewSession(mark);
        if (outcome === "renewed") return send(path, options, accept, true);
        if (outcome === "unreachable") {
          throw new ApiError({ status: 0, code: "NETWORK", serverMessage: null, requestId: error.requestId });
        }
      }
      announceExpiry();
    }
    throw error;
  }
  return response;
}

/* ------------------------------------------------------------------ public surface */

export const api = {
  /** Unwraps to `data`. Use when the response carries no meta worth reading. */
  async get<T>(path: string, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return (await request<T>(path, { ...options, method: "GET" })).data;
  },

  async post<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return (await request<T>(path, { ...options, method: "POST", body })).data;
  },

  async patch<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return (await request<T>(path, { ...options, method: "PATCH", body })).data;
  },

  async put<T>(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<T> {
    return (await request<T>(path, { ...options, method: "PUT", body })).data;
  },

  /**
   * `body` (ik-ek 6 Ekim 2026) yalnız gerekçe isteyen kaldırma uçları için: bir ek bağını
   * kaldırmak `{ reason }` gövdesi istiyor (`DELETE /hr/asset-assignments/{id}/attachments/{linkId}`).
   * Gerekçe sorgu dizesine konmaz — ters vekil günlüğüne ve tarayıcı geçmişine düşerdi.
   */
  async delete<T = void>(path: string, options?: Omit<RequestOptions, "method">): Promise<T> {
    return (await request<T>(path, { ...options, method: "DELETE" })).data;
  },

  /**
   * JSON ZARFI OLMAYAN bir cevabın baytları — bugün tek çağıranı karantina indirmesi.
   *
   * Neden `fetch`, bir bağlantı değil: o uç `POST` ve olması gerektiği gibi öyle. Onay ve gerekçe
   * sorgu dizesindeyken tek bir adrese sığıyordu, yani `SameSite=lax` çerezleriyle başka bir
   * siteden tıklatılan bir bağlantı zararlı baytları yöneticinin makinesine indirebiliyor ve
   * saldırganın yazdığı gerekçeyi onun adına denetim kaydına geçirebiliyordu. Yazan yöntem
   * `CsrfGuard`'dan geçiyor, gövde bir bağlantıya sığmıyor.
   *
   * Hata yolu diğer çağrılarla AYNI: düşen cevap `ApiError` oluyor, yani çağıran ekran zaten
   * bildiği `messageFor(error)` ile konuşuyor.
   */
  async blob(path: string, body?: unknown, options?: Omit<RequestOptions, "method" | "body">): Promise<Blob> {
    const response = await send(path, { ...options, method: "POST", body }, "application/octet-stream");
    const bytes = await response.blob();
    announceSettledWrite(path, { ...options, method: "POST" });
    return bytes;
  },

  /**
   * Gövdesi JSON OLMAYAN bir OKUMA — uygulama içi önizlemenin baytları (Dosyalarım 21).
   *
   * `blob()`'un GET'i: o uç bir yazma (`POST`, karantina indirmesi), bu ise düz bir okuma ve
   * `POST` göndermek `CsrfGuard`'a gereksiz bir tur attırırdı.
   *
   * NEDEN ADRESİ DOĞRUDAN `<object data=…>`'A VERMİYORUZ. Verilebilirdi — uç aynı kökende ve
   * çerez kendiliğinden gidiyor. Ama o yolda 403, 404 ve «bu tür önizlenemiyor» (422)
   * yanıtlarının hepsi BOŞ BİR ÇERÇEVE olarak görünür: tarayıcı gömülü nesnenin hata gövdesini
   * çizmez ve sayfaya haber vermez. Baytları burada okumak, o üç cevabı ekranın söyleyebildiği
   * bir hataya çeviriyor — «hata asla boş durum gibi çizilmez» kuralının bu ekrandaki karşılığı.
   *
   * `accept` çağırandan: PDF ile `image/*` aynı yoldan iniyor ama kabul başlıkları farklı.
   */
  async getBlob(path: string, accept = "*/*", options?: Omit<RequestOptions, "method" | "body">): Promise<Blob> {
    const response = await send(path, { ...options, method: "GET" }, accept);
    return response.blob();
  },

  /**
   * A paged list, flattened into `{ rows, pagination }`.
   *
   * The pagination meta is lifted out of `meta.pagination` here rather than in every table, so
   * a change to the envelope is one edit. A response that claims to be paged but carries no
   * pagination meta gets a synthesised single page rather than a crash — an older endpoint
   * should not take a table down.
   */
  async page<T>(path: string, options?: Omit<RequestOptions, "method" | "body">): Promise<Page<T>> {
    const envelope = await request<T[]>(path, { ...options, method: "GET" });
    const rows = envelope.data ?? [];
    const meta = envelope.meta?.pagination as PaginationMeta | undefined;
    const envelopeMeta: Record<string, unknown> = envelope.meta ?? {};
    const { pagination: _pagination, ...rest } = envelopeMeta;
    return {
      rows,
      pagination:
        meta ??
        {
          page: 1,
          limit: rows.length,
          total: rows.length,
          totalPages: 1,
          hasNext: false,
          hasPrev: false,
        },
      ...(Object.keys(rest).length > 0 ? { meta: rest } : {}),
    };
  },

  /**
   * Every page of a list, up to an explicit cap.
   *
   * WHY THIS EXISTS. The server CLAMPS `limit` to 100 rather than rejecting it, so a caller
   * asking for 200 gets 100 rows and a 200 OK — a silent truncation. A dashboard chart built
   * on that under-reports the moment a company passes a hundred records, and nothing on the
   * screen says so. That is the worst kind of wrong number: confidently displayed.
   *
   * So the pages are walked, and the walk is BOUNDED and HONEST. `maxPages` is the ceiling;
   * `truncated` says whether it was hit. A caller that gets `truncated: true` has to tell the
   * reader — silently drawing a partial chart is the bug this was written to remove.
   *
   * Not for tables. A table pages because a person cannot read ten thousand rows; this is for
   * an aggregate that genuinely needs the whole set, and it stays a stopgap until the server
   * grows a summary endpoint for it.
   */
  async pageAll<T>(
    path: string,
    options?: Omit<RequestOptions, "method" | "body"> & { maxPages?: number },
  ): Promise<{ rows: T[]; total: number; truncated: boolean }> {
    const maxPages = options?.maxPages ?? 10;
    const params = { ...(options?.params ?? {}) };
    const rows: T[] = [];
    let page = 1;
    let total = 0;

    for (; page <= maxPages; page += 1) {
      const result = await this.page<T>(path, {
        ...options,
        params: { ...params, page, limit: 100 },
      });
      rows.push(...result.rows);
      total = result.pagination.total;
      if (!result.pagination.hasNext) break;
    }

    return { rows, total, truncated: rows.length < total };
  },

  /** A cursor list — ledgers, audit logs, notifications. */
  async cursor<T>(path: string, options?: Omit<RequestOptions, "method" | "body">): Promise<CursorPage<T>> {
    const envelope = await request<T[]>(path, { ...options, method: "GET" });
    const cursor = envelope.meta?.cursor as { next: string | null; hasMore: boolean } | undefined;
    return {
      rows: envelope.data ?? [],
      next: cursor?.next ?? null,
      hasMore: cursor?.hasMore ?? false,
    };
  },

  /** Full envelope, for the rare caller that needs `meta` alongside `data`. */
  raw: request,
};
